import SuggestFromHistoryButton from "./ai/SuggestFromHistoryButton";
import { ruleKeys } from "./engine";
import { AT_SIGHT_PROFILE, describeProfile } from "./profile";
import { competenceText, isAiSource, money } from "./format";

const TARGET_KIND_FILTERS = [
  { value: "all", label: "Visão geral" },
  { value: "resource_group", label: "Grupos" },
  { value: "resource", label: "Insumos" },
  { value: "node", label: "Níveis da EAP" },
];

const SOURCE_FILTERS = [
  { value: "all", label: "Todas as origens" },
  { value: "manual", label: "Manual" },
  { value: "ai", label: "IA" },
];

const sourceBadge = (source) => (isAiSource(source) ? "IA" : "manual");

const criterionText = (criterion) => {
  if (criterion?.kind === "explicit") {
    return `${
      (criterion.resourceIds ?? []).length
    } insumo(s) escolhidos a dedo`;
  }
  if (criterion?.kind === "description_contains") {
    return `insumos cuja descrição contém "${criterion.value}"`;
  }
  return "critério não informado";
};

const safeProfileText = (profile) => {
  try {
    return describeProfile(profile ?? AT_SIGHT_PROFILE);
  } catch {
    // Uma regra gravada antes de uma mudança de validação não pode derrubar a lista.
    return "forma de desembolso inválida";
  }
};

const countText = (count, singular, plural, prefix = "") =>
  Number.isSafeInteger(count) && count >= 0
    ? `${prefix}${count} ${count === 1 ? singular : plural}`
    : "Quantidade indisponível";

const updatedAtText = (item) => {
  const date = item.updatedAt ?? item.createdAt;
  if (!date) return null;
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return null;
  const label = item.updatedAt ? "Atualizada em" : "Criada em";
  return `${label} ${parsed.toLocaleDateString("pt-BR")}`;
};

const HistorySuggestion = ({ workId, target, onSuggestion }) =>
  workId && /^resource_group:(erp|ampli):.+/.test(target?.key ?? "") ? (
    <SuggestFromHistoryButton
      workId={workId}
      targetKey={target.key}
      targetLabel={target.label}
      onSuggestion={(proposal) =>
        onSuggestion?.({
          ...proposal,
          workId,
          target: {
            ...target,
            kind: "resource_group",
            level: "resource_group",
          },
          source: "ai_suggestion",
        })
      }
    />
  ) : null;

const RuleRow = ({
  item,
  readOnly,
  onEditRule,
  onToggleRule,
  workId,
  onSuggestion,
}) => {
  const authorText = updatedAtText(item);
  return (
    <li className="ampli-rule-row" data-testid={`row-${item.rowId}`}>
      <div className="ampli-rule-row__identity">
        <strong className="ampli-rule-row__label">
          {item.target?.label ?? item.target?.key}
        </strong>
        {item.target?.path ? (
          <span className="ampli-rule-row__path">{item.target.path}</span>
        ) : null}
        <span className="ampli-rule-row__profile">
          {safeProfileText(item.profile)}
        </span>
        {item.description ? (
          <span className="ampli-rule-row__description">
            {item.description}
          </span>
        ) : null}
        {item.inheritedFrom ? (
          <span className="ampli-rule-row__inherited">{`herdado de ${item.inheritedFrom}`}</span>
        ) : null}
        {item.priceInformed ? (
          <span className="ampli-rule-row__note">
            preço informado · regra no serviço
          </span>
        ) : null}
      </div>
      <div className="ampli-rule-row__facts">
        <span className="ampli-rule-row__count">
          {countText(item.itemCount, "item", "itens", "cobre ")}
        </span>
        <span className="ampli-rule-row__value">
          <span>{money(item.coveredCents)}</span>
        </span>
        <span className="ampli-rule-row__source">
          {sourceBadge(item.source)}
        </span>
        {authorText ? (
          <span className="ampli-rule-row__author">{authorText}</span>
        ) : null}
        {!item.active ? (
          <span className="ampli-rule-row__inactive">inativa</span>
        ) : null}
      </div>
      {!readOnly ? (
        <div className="ampli-rule-row__actions">
          <button
            type="button"
            className="button button-secondary small"
            onClick={() => onEditRule?.(item)}
          >
            {item.inheritedFrom ? "Sobrescrever aqui" : "Editar"}
          </button>
          <HistorySuggestion
            workId={workId}
            target={{ ...item.target, plannedCents: item.coveredCents }}
            onSuggestion={onSuggestion}
          />
          <button
            type="button"
            className="button button-secondary small"
            onClick={() => onToggleRule?.(item, !item.active)}
          >
            {item.active ? "Desativar" : "Ativar"}
          </button>
        </div>
      ) : null}
    </li>
  );
};

const GroupRow = ({ item, readOnly, onEditGroup, workId, onSuggestion }) => (
  <li
    className="ampli-rule-row ampli-rule-row--group"
    data-testid={`row-${item.rowId}`}
  >
    <div className="ampli-rule-row__identity">
      <strong className="ampli-rule-row__label">{item.name}</strong>
      <span className="ampli-rule-row__description">
        {criterionText(item.criterion)}
      </span>
      {item.dynamic ? (
        <span className="ampli-rule-row__note">reavaliado a cada geração</span>
      ) : null}
    </div>
    <div className="ampli-rule-row__facts">
      <span className="ampli-rule-row__count">
        {countText(item.memberCount, "insumo", "insumos")}
      </span>
      <span className="ampli-rule-row__value">
        <span>{money(item.coveredCents)}</span>
      </span>
      <span className="ampli-rule-row__source">grupo</span>
    </div>
    {!readOnly ? (
      <div className="ampli-rule-row__actions">
        <button
          type="button"
          className="button button-secondary small"
          onClick={() => onEditGroup?.(item)}
        >
          Editar grupo
        </button>
        <HistorySuggestion
          workId={workId}
          target={{
            key: ruleKeys.ampliGroup(item.groupId),
            label: item.name,
            plannedCents: item.coveredCents,
          }}
          onSuggestion={onSuggestion}
        />
      </div>
    ) : null}
  </li>
);

const ErpGroupRow = ({
  item,
  readOnly,
  onDefineRule,
  workId,
  onSuggestion,
}) => (
  <li
    className="ampli-rule-row ampli-rule-row--pending"
    data-testid={`row-${item.rowId}`}
  >
    <div className="ampli-rule-row__identity">
      <strong className="ampli-rule-row__label">{item.label}</strong>
      <span className="ampli-rule-row__profile">à vista (padrão)</span>
    </div>
    <div className="ampli-rule-row__facts">
      <span className="ampli-rule-row__count">
        {countText(item.itemCount, "item", "itens", "cobre ")}
      </span>
      <span className="ampli-rule-row__value">
        <span>{money(item.coveredCents)}</span>
      </span>
    </div>
    {!readOnly ? (
      <div className="ampli-rule-row__actions">
        <button
          type="button"
          className="button button-accent small"
          onClick={() =>
            onDefineRule?.({
              kind: "resource_group",
              key: item.key,
              level: "resource_group",
              label: item.label,
            })
          }
        >
          Definir
        </button>
        <HistorySuggestion
          workId={workId}
          target={{
            key: item.key,
            label: item.label,
            plannedCents: item.coveredCents,
          }}
          onSuggestion={onSuggestion}
        />
      </div>
    ) : null}
  </li>
);

// As chamadas de `money()` ficam no TOPO do corpo, nunca atrás de um `?:`: ele lê
// `useProjectContext` por dentro, então um curto-circuito mudaria a contagem de hooks
// desta linha assim que `exceedsCents` virasse 0 (o "change in the order of Hooks").
const AdjustmentRow = ({ item, readOnly, onRemoveAdjustment }) => {
  const movedText = money(item.amountCents);
  const exceededText = money(item.exceedsCents);
  // `appliedCents` distingue duas coisas: `null` é "nenhuma curva foi gerada para este
  // ajuste ainda" (o back-end é explícito nisso) e AUSENTE é uma linha que nem carrega
  // o campo (forma de lista única). O `null` vira "—" — cair no `amountCents` diria
  // que o motor já aplicou o valor; a ausência é que cai no valor pedido.
  const appliedText = money(
    item.appliedCents === undefined ? item.amountCents : item.appliedCents,
  );
  return (
    <li
      className="ampli-rule-row ampli-rule-row--adjustment"
      data-testid={`row-${item.rowId}`}
    >
      <div className="ampli-rule-row__identity">
        <span className="ampli-rule-row__badge">ajuste</span>
        <strong className="ampli-rule-row__label">
          {item.label ?? item.wbsCodeId}
        </strong>
        <span className="ampli-rule-row__profile">
          {`Mover ${movedText} de ${competenceText(
            item.fromCompetence,
          )} para ${competenceText(item.toCompetence)}`}
        </span>
        {item.exceedsCents > 0 ? (
          <span className="ampli-rule-row__warning" role="status">
            {`${exceededText} não couberam no mês de origem e continuam onde estavam.`}
          </span>
        ) : null}
      </div>
      <div className="ampli-rule-row__facts">
        <span className="ampli-rule-row__value">
          <span>{appliedText}</span>
        </span>
      </div>
      {!readOnly ? (
        <div className="ampli-rule-row__actions">
          <button
            type="button"
            className="button button-error small"
            onClick={() => onRemoveAdjustment?.(item)}
          >
            Remover
          </button>
        </div>
      ) : null}
    </li>
  );
};

const DisbursementList = ({
  catalogMode = false,
  workId,
  onSuggestion,
  items = [],
  loading = false,
  error = false,
  readOnly = false,
  filters = { targetKind: "all", source: "all" },
  onFiltersChange,
  onEditRule,
  onDefineRule,
  onEditGroup,
  onToggleRule,
  onRemoveAdjustment,
}) => (
  <section
    className="ampli-disbursement-list"
    aria-label="Regras, grupos e ajustes de desembolso"
  >
    <p>
      Visão geral reúne regras, grupos e ajustes. Selecione Insumos para
      consultar o catálogo completo, com ou sem regra.
    </p>
    <div className="ampli-disbursement-list__filters">
      <label className="ampli-disbursement-list__filter">
        <span>Filtrar por tipo de alvo</span>
        <select
          aria-label="Filtrar por tipo de alvo"
          value={filters.targetKind}
          onChange={(event) =>
            onFiltersChange?.({ ...filters, targetKind: event.target.value })
          }
        >
          {TARGET_KIND_FILTERS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
      {!catalogMode ? (
        <label className="ampli-disbursement-list__filter">
          <span>Filtrar por origem</span>
          <select
            aria-label="Filtrar por origem"
            value={filters.source}
            onChange={(event) =>
              onFiltersChange?.({ ...filters, source: event.target.value })
            }
          >
            {SOURCE_FILTERS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      ) : null}
    </div>

    {!catalogMode && loading ? (
      <p className="card-label margin-top-16">Carregando…</p>
    ) : null}
    {!catalogMode && error && !loading ? (
      <p className="empty-state margin-top-16" role="alert">
        Não foi possível carregar as regras de desembolso. Tente novamente.
      </p>
    ) : null}
    {!catalogMode && !loading && !error && items.length === 0 ? (
      <p className="empty-state margin-top-16">
        Nenhuma regra, grupo ou ajuste neste filtro. Sem regra, todo o previsto
        sai à vista.
      </p>
    ) : null}

    {!catalogMode && items.length > 0 ? (
      <ul className="ampli-disbursement-list__rows">
        {items.map((item) => {
          if (item.kind === "group") {
            return (
              <GroupRow
                key={item.rowId}
                item={item}
                readOnly={readOnly}
                onEditGroup={onEditGroup}
                workId={workId}
                onSuggestion={onSuggestion}
              />
            );
          }
          if (item.kind === "erp_group_without_rule") {
            return (
              <ErpGroupRow
                key={item.rowId}
                item={item}
                readOnly={readOnly}
                onDefineRule={onDefineRule}
                workId={workId}
                onSuggestion={onSuggestion}
              />
            );
          }
          if (item.kind === "adjustment") {
            return (
              <AdjustmentRow
                key={item.rowId}
                item={item}
                readOnly={readOnly}
                onRemoveAdjustment={onRemoveAdjustment}
              />
            );
          }
          return (
            <RuleRow
              key={item.rowId}
              item={item}
              readOnly={readOnly}
              onEditRule={onEditRule}
              onToggleRule={onToggleRule}
              workId={workId}
              onSuggestion={onSuggestion}
            />
          );
        })}
      </ul>
    ) : null}
  </section>
);

export default DisbursementList;
